import { useToggleState } from '@medusajs/ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { EnrichedOrderWithPayment } from '@marxbiotech/shared-types/bff';
import { useTenant } from '@lib/context/tenant-context';
import {
  settlementRequestsQueryOptions,
  type SettlementRequest,
} from '@lib/query/options/payment';
import { isLoanRequestPending } from '@lib/util/loan-request';
import { isOrderCancelable } from '@lib/util/order-cancel';

import { OrderCancelButton } from './button';
import { OrderCancelModal } from './modal';

interface OrderCancelContainerProps {
  order: EnrichedOrderWithPayment;
  orderNumber: string;
}

export const OrderCancelContainer = ({
  order,
  orderNumber,
}: OrderCancelContainerProps) => {
  const { tenant } = useTenant();
  const featureEnabled = tenant?.orderCancelEnabled ?? false;

  const {
    state: isModalOpen,
    open: openModal,
    close: closeModal,
  } = useToggleState(false);

  const cancelable = isOrderCancelable(order);
  const loanRequest = order.marxPayment?.loanRequest;
  const hasPendingLoanRequest =
    !!loanRequest && isLoanRequestPending(loanRequest);

  // 僅在 feature flag 開啟、訂單可取消且有 PENDING LoanRequest 時，才需要進一步查 SettlementRequests。
  // 其他情況 query 透過 enabled: false 跳過請求。
  const { data: settlementRequests = [] } = useQuery({
    ...settlementRequestsQueryOptions(loanRequest?.id ?? ''),
    enabled:
      featureEnabled && cancelable && hasPendingLoanRequest && !!loanRequest?.id,
  });

  const hasPendingSettlementRequest = useMemo(
    () =>
      settlementRequests.some(
        (sr: SettlementRequest) =>
          sr.approvedAt === null && sr.rejectedAt === null
      ),
    [settlementRequests]
  );

  if (!featureEnabled) {
    return null;
  }

  if (!cancelable) {
    return null;
  }

  // 已提交且尚未審核的繳款通知存在時隱藏取消按鈕：
  // 此時顧客正處在「繳款通知」流程中，訂單取消 = 全部退款，
  // 若顧客真正想做的是補繳尾款，應走繳款通知流程而非取消。
  if (hasPendingSettlementRequest) {
    return null;
  }

  return (
    <>
      <OrderCancelButton onClick={openModal} />
      <OrderCancelModal
        isOpen={isModalOpen}
        onClose={closeModal}
        orderId={order.id}
        orderNumber={orderNumber}
      />
    </>
  );
};

export default OrderCancelContainer;
